import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { ResolvedEntitlements } from '@/lib/types/entitlements.types';

interface Envelope<T> {
  success: boolean;
  data?: T;
  message?: string;
}

export const entitlementsApi = createApi({
  reducerPath: 'entitlementsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api',
    credentials: 'include',
  }),
  tagTypes: ['Entitlements'],
  /**
   * Opted in at the API level so every consumer inherits it; individual hooks
   * can still turn it off. `setupListeners` is wired in store.ts, which is what
   * makes both of these fire at all.
   */
  refetchOnFocus: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    /**
     * The current user's entitlements — every catalogued feature's decision,
     * resolved server-side against their tier and any per-account exception.
     *
     * Entitlements are not static, which the previous 24-hour cache assumed:
     * an admin raising a tier's ceiling, or granting one customer an
     * exception, changes them mid-session, and that window meant the customer
     * had to sign out before they saw it.
     *
     * `keepUnusedDataFor` only governs how long the result survives with no
     * subscriber, and the provider holds a subscription for the whole session,
     * so it is not what makes this fresh — the focus/reconnect refetches above
     * and the provider's periodic revalidation are. It is shortened anyway so
     * a sign-out and back in re-reads rather than replaying yesterday's
     * answer. Upstream the request costs two queries.
     */
    getMyEntitlements: builder.query<ResolvedEntitlements | null, void>({
      query: () => '/credit/entitlements',
      transformResponse: (response: Envelope<ResolvedEntitlements | null>) =>
        response?.data ?? null,
      providesTags: ['Entitlements'],
      keepUnusedDataFor: 5 * 60,
    }),
  }),
});

export const { useGetMyEntitlementsQuery } = entitlementsApi;
